import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { PaginationBar } from '../common/PaginationBar';
import { CUSTOMER_LIST, DOCUMENTS, INFO, ROLES, ROUTES, SORT_TYPE } from '../../../constants';
import { CustomerDetailsTab } from '../../Admin';
import { DocumentDetailsTab } from '../DocumentDetailsTab';
import { handleScroll, sortingByName } from '../../../utils';
import { isEmpty, isNumber } from 'lodash';
import useIsScrollable from '../../../hooks/useIsScrollable';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import CircleSpinner from '../../../assets/svg/CircleSpinner';

export const Table = ({
    columns,
    data,
    pagination,
    pageName,
    debouncedApiCall,
    search,
    selectedTabData,
    tableStyle = {},
    stickyColumns = [],
    onDragEnd = () => {},
    isDraggable = false,
    tableClass = '',
    initialSort = null,
    columnToSort = null,
    hidePagination = false,
    isLoading = false,
    setServerSort,
    columnsForServerSort = [],
}) => {
    const location = useLocation();
    const initialOpenTab = useSelector((s) => s.search.sidePanel);
    const initialId = useSelector((s) => s.search.selectedRowId);
    const [noDataFound, setNoDataFound] = useState(false);
    const [tabData, setTabData] = useState(initialId);
    const [tabOpen, setTabOpen] = useState(initialOpenTab);
    const [tabDocumentOpen, setDocumentTabOpen] = useState(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);
    const [sort, setSort] = useState('DEFAULT');
    const [sortedData, setSortedData] = useState(data);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollBar = useRef(null);
    const tableContainerRef = useRef(null);
    const { isScrollableWidth } = useIsScrollable(tableContainerRef);
    const role = useSelector((s) => s.user.role);
    const showDetailTabs = ![ROLES.SALES_AGENT].includes(role);

    useEffect(() => {
        setTabOpen(initialOpenTab);
        if (data && data.length > 0) {
            setNoDataFound(false);
        } else {
            setNoDataFound(true);
        }
    }, [data, initialOpenTab]);

    useEffect(() => {
        setTabOpen(false);
        setDocumentTabOpen(false);
        setSelectedRowIndex(0);
    }, [pagination?.currentPage, search]);

    const handleSort = (sortType, index) => {
        const dataFieldName = columns[index]?.dataField[0];
        if (
            setServerSort &&
            columnsForServerSort.length > 0 &&
            columnsForServerSort.includes(dataFieldName)
        ) {
            setServerSort({ sort: sortType, column: dataFieldName });
            return;
        }
        if (sortType === SORT_TYPE.ASC) {
            setSortedData(sortingByName({ data: data, sortBy: dataFieldName, order: 0 }));
            setSort(sortType);
        } else if (sortType === SORT_TYPE.DESC) {
            setSortedData(sortingByName({ data: data, sortBy: dataFieldName, order: 1 }));
            setSort(sortType);
        }
    };

    const handleScrolling = () => {
        handleScroll(setIsScrolling, scrollBar, 2000);
    };

    const handleTabOpen = (data) => {
        if (pageName === CUSTOMER_LIST.PAGE_NAME) {
            setTabData(data.id); // Data will be a ID in this case
            setTabOpen(true);
            setSelectedRowIndex(1);
        } else if (pageName === DOCUMENTS.PAGE_NAME) {
            setTabData(data); // Data will be an object in this case
            setDocumentTabOpen(true);
            setSelectedRowIndex(1);
        }
    };

    useEffect(() => {
        if (
            initialSort &&
            [SORT_TYPE.ASC, SORT_TYPE.DESC].includes(initialSort) &&
            isNumber(columnToSort) &&
            columnToSort >= 0
        ) {
            setSortedData(
                sortingByName({
                    data: data,
                    sortBy: columns[columnToSort]?.dataField[0],
                    order: initialSort === SORT_TYPE.ASC ? 0 : 1,
                }),
            );
            setSort(initialSort);
        } else {
            setSortedData(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, initialSort, columnToSort]);

    useEffect(() => {
        if (selectedTabData && selectedTabData !== '' && !isEmpty(selectedTabData)) {
            handleTabOpen(selectedTabData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTabData]);

    return (
        <>
            {!hidePagination && !noDataFound && (
                <PaginationBar pageName={pageName} pagination={pagination} />
            )}
            <section id='Table' className='position-relative'>
                <div
                    onScroll={handleScrolling}
                    onMouseMove={handleScrolling}
                    className={
                        'ssc-table-area w-100 position-relative ' +
                        (location.pathname === ROUTES.ADMIN.ADMIN_SCORE_CARD_LISTING
                            ? ' custom-table-height-sc'
                            : '') +
                        (isScrolling ? ' scrolling' : ' not-scrolling')
                    }
                    style={tableStyle}
                    ref={tableContainerRef}
                >
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId='Table'>
                            {(provided) => (
                                <table
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className={`w-100 + ${tableClass}`}
                                >
                                    <TableHeader
                                        handleSort={handleSort}
                                        sort={sort}
                                        columns={columns}
                                        data={sortedData}
                                        stickyColumns={stickyColumns}
                                        isScrollableWidth={isScrollableWidth}
                                    />
                                    <TableBody
                                        pageName={pageName}
                                        columns={columns}
                                        data={sortedData}
                                        setTabData={setTabData}
                                        setTabOpen={setTabOpen}
                                        debouncedApiCall={debouncedApiCall}
                                        setDocumentTabOpen={setDocumentTabOpen}
                                        selectedRowIndex={
                                            tabOpen || tabDocumentOpen ? selectedRowIndex : 0
                                        }
                                        setSelectedRowIndex={setSelectedRowIndex}
                                        stickyColumns={stickyColumns}
                                        isScrollableWidth={isScrollableWidth}
                                        isDraggable={isDraggable}
                                        pagination={pagination}
                                    />
                                </table>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {noDataFound && !isLoading && (
                        <div
                            className='mt-4 vh-50 d-flex justify-content-center'
                            style={{
                                fontSize: '16px',
                            }}
                        >
                            {INFO.NO_DATA_AVAILABLE}
                        </div>
                    )}
                    {isLoading && <CircleSpinner absolute size='2.75rem' />}
                </div>
                {tabOpen && showDetailTabs && location.pathname === ROUTES.ADMIN.CUSTOMER && (
                    <CustomerDetailsTab
                        tabOpen={tabOpen}
                        setTabOpen={setTabOpen}
                        setSelectedRowIndex={setSelectedRowIndex}
                        tabData={tabData}
                        debouncedApiCallFromParent={debouncedApiCall}
                    />
                )}
                {tabDocumentOpen &&
                    showDetailTabs &&
                    (location.pathname === ROUTES.ADMIN.DOCUMENTS ||
                        location.pathname === ROUTES.CUSTOMER.DOCUMENTS) && (
                        <DocumentDetailsTab
                            setDocumentTabOpen={setDocumentTabOpen}
                            setSelectedRowIndex={setSelectedRowIndex}
                            tabData={tabData}
                            debouncedApiCallFromParent={debouncedApiCall}
                        />
                    )}
            </section>
        </>
    );
};

Table.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    pagination: PropTypes.shape({
        dataLength: PropTypes.number.isRequired,
        dataCount: PropTypes.number.isRequired,
        currentPage: PropTypes.number.isRequired,
        itemsPerPage: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        handlePrevPage: PropTypes.func.isRequired,
        handleNextPage: PropTypes.func.isRequired,
    }).isRequired,
    pageName: PropTypes.string.isRequired,
    debouncedApiCall: PropTypes.func,
    setServerSort: PropTypes.func,
    onDragEnd: PropTypes.func,
    search: PropTypes.string,
    selectedTabData: PropTypes.object,
    tableStyle: PropTypes.object,
    initialSort: PropTypes.oneOf([SORT_TYPE.ASC, SORT_TYPE.DESC]),
    columnToSort: PropTypes.number,
    hidePagination: PropTypes.bool,
    isLoading: PropTypes.bool,
};
